/**
 * Gapless playback of streamed PCM16 speech (server: services/ttsService.ts —
 * raw 16-bit little-endian mono, 24 kHz). Each WebSocket frame is converted to
 * an AudioBuffer and scheduled to start exactly when the previous one ends, so
 * speech plays while later audio is still arriving. No decoding step.
 */

export const PCM_SAMPLE_RATE = 24_000
/** Small lead before the first frame (and after an underrun) to absorb network jitter. */
const LEAD_SECONDS = 0.08

export interface PcmPlayer {
  /** Schedule one PCM16 frame after everything already scheduled. */
  play(pcm: ArrayBuffer): void
  /** Stop everything immediately (barge-in, hang-up). */
  stop(): void
  /** True while any scheduled audio has not finished playing. */
  readonly busy: boolean
  /** Called when the last scheduled frame finishes. */
  onIdle: (() => void) | null
}

export function createPcmPlayer(ctx: AudioContext, output: AudioNode, sampleRate = PCM_SAMPLE_RATE): PcmPlayer {
  const live = new Set<AudioBufferSourceNode>()
  let nextStart = 0

  const player: PcmPlayer = {
    onIdle: null,
    get busy() { return live.size > 0 },

    play(pcm: ArrayBuffer) {
      const samples = new Int16Array(pcm, 0, Math.floor(pcm.byteLength / 2))
      if (samples.length === 0) return
      const floats = new Float32Array(samples.length)
      for (let i = 0; i < samples.length; i++) floats[i] = samples[i] / 32768

      const buffer = ctx.createBuffer(1, floats.length, sampleRate)
      buffer.copyToChannel(floats, 0)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(output)

      // Back-to-back with the previous frame; resync with a small lead after an underrun.
      const startAt = Math.max(ctx.currentTime + LEAD_SECONDS, nextStart)
      source.start(startAt)
      nextStart = startAt + buffer.duration

      live.add(source)
      source.onended = () => {
        live.delete(source)
        if (live.size === 0) player.onIdle?.()
      }
    },

    stop() {
      for (const s of live) {
        s.onended = null
        try { s.stop() } catch { /* already stopped */ }
      }
      live.clear()
      nextStart = 0
    },
  }
  return player
}
